// Inline, non-blocking "couldn't refresh — Retry" affordance for dashboard
// widgets whose data fetch failed (usually a background RPC that timed out).
//
// Why it exists: the dashboard is a glance-and-go screen (~6s dwell). Before
// this, a widget whose refresh timed out either silently kept stale numbers
// with no hint, or showed a misleading empty state — and the operator's only
// known recovery was a full-page reload (the single biggest reload source in
// the UX telemetry, UX-007). This gives a one-tap, in-place retry instead.
//
// `stale` = we're still showing last-known data (React Query keeps the prior
// result on error) → say so, so the numbers aren't trusted as live. Otherwise
// there's nothing to show and it reads as "Couldn't load".

import { RefreshCw } from 'lucide-react'
import clsx from 'clsx'

export default function WidgetRetry({
  onRetry,
  busy = false,
  stale = false,
  noun,
  className,
  surface = 'dark',
}: {
  onRetry: () => void
  busy?: boolean
  stale?: boolean
  /** What failed, for the label: "Couldn’t load actuals · Retry". */
  noun?: string
  className?: string
  /** What the pill sits on — `dark` (default) is the app shell, `light` a
   *  data-theme="light" page, where amber-200 on a white page is ~2:1. */
  surface?: 'dark' | 'light'
}) {
  return (
    <button
      type="button"
      onClick={onRetry}
      disabled={busy}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg border',
        surface === 'light'
          ? 'border-warning-edge bg-warning-soft text-warning hover:bg-warning-edge'
          : 'border-amber-800/60 bg-amber-950/40 text-amber-200 hover:bg-amber-900/50',
        'px-2 py-1 coarse:min-h-[44px] coarse:px-3 text-[11px] disabled:opacity-60 transition-colors',
        className,
      )}
    >
      <RefreshCw size={12} className={busy ? 'animate-spin' : undefined} />
      {busy
        ? 'Refreshing…'
        : stale
          ? `Couldn’t refresh${noun ? ` ${noun}` : ''} — showing last known · Retry`
          : `Couldn’t load${noun ? ` ${noun}` : ''} · Retry`}
    </button>
  )
}
